import { useTranslation } from 'react-i18next';

import type { IncidentListItem, IncidentSeverity } from '../../../api/types';
import { Badge, type BadgeTone } from '../../../shared/ui/Badge';
import { EmptyState } from '../../../shared/ui/EmptyState';
import { Table } from '../../../shared/ui/Table';
import styles from './IncidentTable.module.css';

// AC-13: критичность выделена цветовым бейджем — все четыре уровня разными
// тонами и по убыванию визуального веса, иначе ранжирование не читается
// глазами: alert (глина) > caution (охра) > muted (синий) > neutral (серый).
const SEVERITY_TONE: Record<IncidentSeverity, BadgeTone> = {
  CRITICAL: 'alert',
  ERROR: 'caution',
  WARNING: 'muted',
  INFO: 'neutral',
};

interface IncidentTableProps {
  incidents: IncidentListItem[];
  selectedId: number | null;
  onSelect: (incidentId: number) => void;
}

// PARA-129, AC-15: время, критичность, категория, тип, сообщение, субъект,
// признак подтверждения. Stack trace в списке не показывается.
export function IncidentTable({
  incidents,
  selectedId,
  onSelect,
}: IncidentTableProps): JSX.Element {
  const { t } = useTranslation();

  if (incidents.length === 0) {
    // AC-20: пустое состояние с пояснением, а не пустая таблица.
    return <EmptyState>{t('monitoring.empty')}</EmptyState>;
  }

  return (
    <Table>
      <thead>
        <tr>
          <th>{t('monitoring.columns.occurredAt')}</th>
          <th>{t('monitoring.columns.severity')}</th>
          <th>{t('monitoring.columns.category')}</th>
          <th>{t('monitoring.columns.incidentType')}</th>
          <th>{t('monitoring.columns.message')}</th>
          <th>{t('monitoring.columns.subject')}</th>
          <th>{t('monitoring.columns.acknowledged')}</th>
        </tr>
      </thead>
      <tbody>
        {incidents.map((incident) => (
          <tr
            key={incident.id}
            className={incident.id === selectedId ? styles.selected : undefined}
          >
            <td className={styles.mono}>{incident.occurred_at}</td>
            <td>
              <Badge tone={SEVERITY_TONE[incident.severity]}>
                {t(`monitoring.severity.${incident.severity}`)}
              </Badge>
            </td>
            <td>{t(`monitoring.category.${incident.category}`)}</td>
            <td>{t(`monitoring.type.${incident.incident_type}`)}</td>
            <td>
              <button
                type="button"
                className={styles.messageButton}
                onClick={() => onSelect(incident.id)}
              >
                {incident.message}
              </button>
            </td>
            <td className={styles.mono}>{incident.subject ?? '—'}</td>
            <td>
              {incident.acknowledged
                ? t('monitoring.acknowledgedYes')
                : t('monitoring.acknowledgedNo')}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
